import { zodResolver } from "@hookform/resolvers/zod";
import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { z } from "zod";
import { getErrorDetails } from "../../app/appUtils";
import AppButton from "../../components/ui/AppButton";
import AppInputField from "../../components/ui/AppInputField";
import AppLogo from "../../components/ui/AppLogo";
import { Spinner } from "../../components/ui/common";
import SplitScreenLayout from "../../components/views/layouts/SplitScreenLayout";
import { useToast } from "../../hooks/useToast";
import { useResetPasswordMutation } from "./authApiSlice";

const resetPasswordSchema = z
  .object({
    password: z.string().min(8, "Password must be at least 8 characters long"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const inputFields = [
  { label: "New Password", name: "password" as const },
  { label: "Confirm Password", name: "confirmPassword" as const },
];

/**
 * Reads reset-link params tolerantly. Email clients can wrap long links, which
 * leaves stray whitespace in keys and values (e.g. `?%20token=...`), and the
 * token is base64 so a literal "+" is data, not a space (URLSearchParams would
 * decode it as one). Neither emails nor tokens contain whitespace, so strip it.
 */
const parseResetParams = (search: string) => {
  const params: Record<string, string> = {};

  search
    .replace(/^\?/, "")
    .split("&")
    .forEach((pair) => {
      const separator = pair.indexOf("=");
      if (separator === -1) return;

      const decode = (part: string) => {
        try {
          return decodeURIComponent(part);
        } catch {
          return part;
        }
      };

      const key = decode(pair.slice(0, separator)).replace(/\s+/g, "");
      params[key.toLowerCase()] = decode(pair.slice(separator + 1)).replace(
        /\s+/g,
        "",
      );
    });

  return params;
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { search } = useLocation();
  const { email = "", token = "" } = useMemo(
    () => parseResetParams(search),
    [search],
  );
  const [resetPassword, { isLoading }] = useResetPasswordMutation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: "onBlur",
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onSubmit = async ({ password }: ResetPasswordFormData) => {
    try {
      const response = await resetPassword({ email, password, token }).unwrap();
      toast.success({
        title: "Password Reset!",
        message: response?.message ?? "You can now sign in.",
      });
      navigate("/sign-in", { replace: true });
    } catch (error: any) {
      toast.error({
        title: "Reset Failed",
        message: getErrorDetails(error).message,
      });
    }
  };

  const isLinkInvalid = !email || !token;

  return (
    <SplitScreenLayout>
      <div className="w-full max-w-md sm:max-w-lg">
        <div className="flex flex-col items-center mb-8">
          <AppLogo />
          <h1 className="text-primary font-semibold text-xl sm:text-2xl mt-4">
            Reset password
          </h1>
          <p className="text-sm sm:text-base text-pColor text-center mt-2">
            {isLinkInvalid
              ? "This reset link is invalid or incomplete."
              : "Choose a new password for your account."}
          </p>
        </div>

        {isLinkInvalid ? (
          <AppButton
            onClick={() => navigate("/forgot-password")}
            className="w-full h-11 sm:h-12"
          >
            Request a new link
          </AppButton>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {inputFields.map(({ label, name }) => (
              <Controller
                key={name}
                control={control}
                name={name}
                render={({ field }) => (
                  <AppInputField
                    label={label}
                    type="password"
                    error={errors[name]?.message}
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            ))}
            <AppButton
              type="submit"
              disabled={isLoading}
              className="w-full h-11 sm:h-12 text-base font-medium"
            >
              {isLoading ? <Spinner /> : "Reset Password"}
            </AppButton>
          </form>
        )}
      </div>
    </SplitScreenLayout>
  );
};

export default ResetPassword;
